import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Lock,
  type LucideIcon,
} from 'lucide-react';
import type {
  MonitoringSource,
  MonitoringSourceKind,
  MonitoringSourceStatus,
  Tribunal,
} from '@/lib/api-client';

/**
 * J08.2 — Apresentação dos chips de fonte (MonitoringSource) na tela
 * de Monitoramentos.
 *
 * O backend grava `value` em dois formatos:
 *   - kind ∈ ('djen','tribunal'): `<tribunal_code>::<subject_id>`
 *     onde tribunal_code é `<ramo>.<TR>` (ex.: "8.21" = TJRS, "4.04" = TRF4).
 *   - kind ∈ ('oab','cpf','cnpj','nome_parte'): o valor direto.
 *
 * Este módulo resolve o tribunal_code via um lookup `Map<code, Tribunal>`
 * (lista de tribunais cacheada via TanStack Query) e devolve label, ícone
 * e cor amigáveis para o chip.
 */

export interface SourceDisplay {
  /** Texto principal do chip, ex.: "DJEN — TJRS". */
  label: string;
  /** Texto secundário (linha de baixo), opcional. */
  subtitle?: string;
  /** Ícone de identidade da fonte (kind). */
  icon: LucideIcon;
  /** Cor base do chip (texto + borda — usa color-mix no consumidor). */
  color: string;
  /** Cor do status (texto e ícone). */
  statusColor: string;
  /** Ícone do status. */
  statusIcon: LucideIcon;
  /** Label do status, ex.: "Monitorando". */
  statusLabel: string;
  /** Tooltip detalhado (usado em <span title="">). */
  tooltip: string;
  /** Grupo lógico para agrupar no header. */
  group: 'djen' | 'tribunal' | 'direto';
}

const SYSTEM_LABEL: Record<string, string> = {
  pje: 'PJE',
  esaj: 'eSAJ',
  eproc: 'EPROC',
  projudi: 'PROJUDI',
  tucujuris: 'Tucujuris',
  themis: 'Themis',
  other: 'Tribunal',
};

const KIND_GROUP_LABEL: Record<MonitoringSourceKind, string> = {
  djen: 'Fontes DJEN',
  tribunal: 'Tribunais',
  oab: 'OAB direto',
  cpf: 'CPF direto',
  cnpj: 'CNPJ (informativo)',
  nome_parte: 'Nome de parte',
};

const STATUS_META: Record<
  MonitoringSourceStatus,
  { icon: LucideIcon; label: string; color: string }
> = {
  monitoring: {
    icon: CheckCircle2,
    label: 'Monitorando',
    color: 'var(--success, #10b981)',
  },
  pending: {
    icon: Clock,
    label: 'Aguardando primeira sync',
    color: 'var(--warning, #f59e0b)',
  },
  no_certificate: {
    icon: Lock,
    label: 'Sem certificado',
    color: 'var(--danger, #ef4444)',
  },
  failure: {
    icon: AlertCircle,
    label: 'Falha',
    color: 'var(--danger, #ef4444)',
  },
};

const KIND_COLOR: Record<MonitoringSourceKind, string> = {
  djen: '#3b82f6', // azul claro
  tribunal: '#a855f7', // roxo
  oab: 'var(--fg-muted)',
  cpf: 'var(--fg-muted)',
  cnpj: 'var(--fg-muted)',
  nome_parte: 'var(--fg-muted)',
};

/**
 * Extrai o tribunal_code do `value` para sources `djen`/`tribunal`.
 *
 * Formato esperado: `<tribunal_code>::<subject_id>`.
 * Retorna null para outros kinds ou se o formato for inesperado.
 */
export function parseTribunalCode(source: MonitoringSource): string | null {
  if (source.tribunalCode) return source.tribunalCode;
  if (source.kind !== 'djen' && source.kind !== 'tribunal') return null;
  const idx = source.value.indexOf('::');
  if (idx <= 0) return null;
  return source.value.slice(0, idx);
}

/**
 * Build amigavel do label de uma fonte segundo o kind:
 *   - djen      → "DJEN — TJRS"   (parseia tribunal_code do value)
 *   - tribunal  → "EPROC TJRS"    (usa primary_system + abbreviation)
 *   - oab       → "OAB 123456/RS"
 *   - cpf/cnpj  → "CPF 999.999.999-99"
 *   - nome_parte→ "Nome: <valor>"
 */
export function describeSource(
  source: MonitoringSource,
  tribunalsMap: Map<string, Tribunal>,
): SourceDisplay {
  const status = STATUS_META[source.status] ?? STATUS_META.pending;
  const baseColor = KIND_COLOR[source.kind] ?? 'var(--fg-muted)';

  let label = '';
  let subtitle: string | undefined;
  let group: SourceDisplay['group'] = 'direto';

  if (source.kind === 'djen') {
    const code = parseTribunalCode(source);
    const tr = code ? tribunalsMap.get(code) : null;
    label = tr ? `DJEN — ${tr.abbreviation}` : 'DJEN';
    subtitle = 'Diário oficial';
    group = 'djen';
  } else if (source.kind === 'tribunal') {
    const code = parseTribunalCode(source);
    const tr = code ? tribunalsMap.get(code) : null;
    if (tr) {
      const system = tr.primarySystem
        ? SYSTEM_LABEL[tr.primarySystem] ?? tr.primarySystem.toUpperCase()
        : 'Tribunal';
      label = `${system} ${tr.abbreviation}`;
      subtitle = tr.level === 'estadual' ? '1° Grau' : tr.name;
    } else {
      label = code ? `Tribunal ${code}` : 'Tribunal';
    }
    group = 'tribunal';
  } else if (source.kind === 'oab') {
    label = `OAB ${source.value}`;
    group = 'direto';
  } else if (source.kind === 'cpf') {
    label = `CPF ${source.value}`;
    group = 'direto';
  } else if (source.kind === 'cnpj') {
    label = `CNPJ ${source.value}`;
    subtitle = 'informativo';
    group = 'direto';
  } else if (source.kind === 'nome_parte') {
    label = `Nome: ${source.value}`;
    group = 'direto';
  } else {
    label = source.value;
  }

  const tooltipParts: string[] = [label];
  if (subtitle) tooltipParts.push(subtitle);
  tooltipParts.push(status.label);
  if (source.errorMessage) tooltipParts.push(`Erro: ${source.errorMessage}`);
  if (!source.active) tooltipParts.push('(inativa)');

  return {
    label,
    subtitle,
    icon: status.icon, // kind icon não usado por enquanto — chip mostra o status
    color: baseColor,
    statusColor: status.color,
    statusIcon: status.icon,
    statusLabel: status.label,
    tooltip: tooltipParts.join(' • '),
    group,
  };
}

export function groupLabel(kind: MonitoringSourceKind): string {
  return KIND_GROUP_LABEL[kind] ?? kind;
}
